// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useQuery } from '@apollo/client';
import { formatTime } from '@seedplanner/shared-utils';
import gql from 'graphql-tag';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router';
import { ShareButtons } from '../components/blog/ShareButtons';
import { UpsellDialog } from '../components/blog/UpsellDialog';
import { PageError } from '../components/common/error';
import { PageLoading } from '../components/common/loading';
import RenderMarkdown from '../components/markdown/RenderMarkdown';
import { useAppContext } from '../context/AppContext';
import { Box, Card, Typography } from '../lib/base';
import { Link } from '../lib/link';
import * as routes from '../routes';
import type { SingleBlogPostQuery, SingleBlogPostQueryVariables } from '../types/__generated__/graphql';
import { getID } from '../utils';

const BLOG_POST_QUERY = gql`
  query SingleBlogPost($slug: String!) {
    blog_post(slug: $slug) {
      id
      title
      seo_title
      author
      description
      seo_description
      content
      updated_at
      created_at
    }
  }
`;

export default function BlogPost() {
  const { slug } = useParams<{ readonly slug: string }>();
  const { options } = useAppContext();
  if (slug === undefined) {
    return <PageError error={new Error('No blog post slug provided')} />;
  }
  const appUrl = options.url;
  const { data, loading, error } = useQuery<SingleBlogPostQuery, SingleBlogPostQueryVariables>(BLOG_POST_QUERY, {
    variables: { slug },
  });

  if (slug === '[object Object]') {
    return <PageError error={new Error('Invalid blog post slug provided!')} />;
  }
  if (error) {
    return <PageError error={error} />;
  }
  if (loading) {
    return <PageLoading />;
  }
  if (data?.blog_post == null) {
    return <PageError error={new Error('No blog post found! This post may have been deleted.')} />;
  }

  const post = data.blog_post;
  const slugIn = getID(post.id);

  const fullPath = new URL(routes.makeBlogTitlePath(slugIn), appUrl).toString();

  return (
    <>
      <Helmet>
        <title>{post.seo_title}</title>
        <meta name="description" content={post.seo_description} />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            author: {
              '@type': 'Person',
              name: post.author,
            },
            datePublished: new Date(post.created_at * 1000).toISOString(),
            dateModified: new Date(post.updated_at * 1000).toISOString(),
            description: post.seo_description,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': fullPath,
            },
            publisher: {
              '@type': 'Organization',
              name: 'Microgreen Manager',
              logo: {
                '@type': 'ImageObject',
                url: 'https://microgreenmanager.com/icon-384x384.png',
                width: 384,
                height: 384,
              },
            },
          })}
        </script>
      </Helmet>
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-start' }}>
        <Link path={routes.BLOG} title="‹ Back to blog" />
      </Box>
      <Card sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', rowGap: 2 }}>
        <Typography component="h2" variant="h2" sx={{ pt: 2, px: 2, textAlign: 'center' }}>
          {post.title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            <time dateTime={new Date(post.created_at * 1000).toISOString()}>
              {formatTime(post.created_at, { timezone: 'Etc/UTC', year: true, removeDayOfWeek: true })}
            </time>
          </Typography>
          <Box sx={{ border: '1px solid grey', my: 0, mx: '25px' }} />
          <Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
            Written by{' '}
            {post.author === 'Garrett Corwin' ? (
              <Link inlineText variant="body2" path={routes.TEAM} title={post.author} />
            ) : (
              post.author
            )}
          </Typography>
        </Box>
        <Box sx={{ px: 2 }}>
          <RenderMarkdown source={post.content} />
        </Box>
        <Typography variant="body2" sx={{ pt: 2, px: 2 }}>
          Share this post:
        </Typography>
        <Box
          sx={{
            pb: 2,
            gap: 2,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <ShareButtons title={post.title} url={fullPath} />
        </Box>
      </Card>
      <UpsellDialog />
    </>
  );
}
